/**
 * Dashboard Dependency Injection
 * Registra e provê todas as dependências da feature Dashboard
 */
import { GetDashboardStatsUseCase } from '../domain/usecases/get_dashboard_stats';
import { GetDashboardMetricsUseCase } from '../domain/usecases/get_dashboard_metrics';
import { DashboardRepository } from '../domain/repositories/dashboard_repository';
import { DashboardRepositoryImpl } from '../data/repositories_impl/dashboard_repository_impl';
import { DashboardRemoteDataSource, DashboardRemoteDataSourceImpl } from '../data/datasources/remote/dashboard_remote_datasource';

/**
 * Dashboard DI Container
 * Gerencia as dependências específicas da feature Dashboard
 */
export class DashboardDI {
  private static instance: DashboardDI;
  private dependencies: Map<string, any> = new Map();

  private constructor() {
    this.initializeDependencies();
  }

  static getInstance(): DashboardDI {
    if (!DashboardDI.instance) {
      DashboardDI.instance = new DashboardDI();
    }
    return DashboardDI.instance;
  }

  /**
   * Inicializa todas as dependências da feature Dashboard
   */
  private initializeDependencies(): void {
    console.log('🏗️ DashboardDI: Inicializando dependências...');

    // Data Sources
    const dashboardRemoteDataSource = new DashboardRemoteDataSourceImpl();
    this.dependencies.set('DashboardRemoteDataSource', dashboardRemoteDataSource);

    // Repositories
    const dashboardRepository = new DashboardRepositoryImpl(dashboardRemoteDataSource);
    this.dependencies.set('DashboardRepository', dashboardRepository);

    // Use Cases
    const getDashboardStatsUseCase = new GetDashboardStatsUseCase(dashboardRepository);
    this.dependencies.set('GetDashboardStatsUseCase', getDashboardStatsUseCase);

    const getDashboardMetricsUseCase = new GetDashboardMetricsUseCase(dashboardRepository);
    this.dependencies.set('GetDashboardMetricsUseCase', getDashboardMetricsUseCase);

    console.log('✅ DashboardDI: Dependências inicializadas');
  }

  /**
   * Obtém uma dependência pelo nome
   */
  get<T>(name: string): T {
    const dependency = this.dependencies.get(name);
    if (!dependency) {
      throw new Error(`DashboardDI: Dependência '${name}' não encontrada`);
    }
    return dependency as T;
  }

  /**
   * Registra uma nova dependência
   */
  register<T>(name: string, instance: T): void {
    this.dependencies.set(name, instance);
    console.log(`📝 DashboardDI: Dependência '${name}' registrada`);
  }

  /**
   * Remove uma dependência
   */
  unregister(name: string): void {
    this.dependencies.delete(name);
    console.log(`🗑️ DashboardDI: Dependência '${name}' removida`);
  }

  /**
   * Lista todas as dependências registradas
   */
  listDependencies(): string[] {
    return Array.from(this.dependencies.keys());
  }

  /**
   * Verifica se uma dependência está registrada
   */
  has(name: string): boolean {
    return this.dependencies.has(name);
  }

  /**
   * Limpa todas as dependências (útil para testes)
   */
  clear(): void {
    this.dependencies.clear();
    console.log('🧹 DashboardDI: Todas as dependências foram limpas');
  }

  /**
   * Recarrega todas as dependências
   */
  reload(): void {
    console.log('🔄 DashboardDI: Recarregando dependências...');
    this.clear();
    this.initializeDependencies();
  }

  /**
   * Health check das dependências críticas
   */
  healthCheck(): { healthy: boolean; issues: string[] } {
    const requiredDependencies = [
      'DashboardRemoteDataSource',
      'DashboardRepository',
      'GetDashboardStatsUseCase',
      'GetDashboardMetricsUseCase'
    ];

    const issues: string[] = [];

    for (const dependency of requiredDependencies) {
      if (!this.has(dependency)) {
        issues.push(`Dependência crítica '${dependency}' não encontrada`);
      }
    }

    const healthy = issues.length === 0;

    if (healthy) {
      console.log('✅ DashboardDI: Health check passou - todas as dependências OK');
    } else {
      console.error('❌ DashboardDI: Health check falhou', issues);
    }

    return { healthy, issues };
  }
}

// ============ CONVENIENCE EXPORTS ============

/**
 * Shortcuts para acessar dependências específicas
 */
export const dashboardDI = DashboardDI.getInstance();

export const getDashboardStatsUseCase = () => 
  dashboardDI.get<GetDashboardStatsUseCase>('GetDashboardStatsUseCase');

export const getDashboardMetricsUseCase = () => 
  dashboardDI.get<GetDashboardMetricsUseCase>('GetDashboardMetricsUseCase');

export const getDashboardRepository = () => 
  dashboardDI.get<DashboardRepository>('DashboardRepository');

export const getDashboardRemoteDataSource = () => 
  dashboardDI.get<DashboardRemoteDataSource>('DashboardRemoteDataSource');
